import { useMemo, useState } from 'react';
import type { BlendMode, PatternLayer } from '@/core/types';
import { BLEND_MODES } from '@/core/blend';
import { listPatterns, requirePattern } from '@/core/patterns/registry';
import { useDesignStore } from '@/state/store';
import { AngleDial, Section, Select, Slider, Toggle } from '../components/Controls';
import { ParamControls } from '../components/ParamControls';
import { Thumbnail } from '../components/Thumbnail';

/**
 * The layer editor.
 *
 * This is the modular story made visible: any registered pattern can be stacked
 * on any other, each with its own blend mode, weight and wall-space transform.
 * The controls inside a layer are generated from the pattern's own schema, so
 * this component never learns what any individual pattern does.
 */
export function LayersTab({ onRequestImage }: { onRequestImage(): void }) {
  const design = useDesignStore((state) => state.design);
  const addLayer = useDesignStore((state) => state.addLayer);
  const updateLayer = useDesignStore((state) => state.updateLayer);
  const updateLayerParams = useDesignStore((state) => state.updateLayerParams);
  const removeLayer = useDesignStore((state) => state.removeLayer);
  const moveLayer = useDesignStore((state) => state.moveLayer);

  const [expanded, setExpanded] = useState<string | null>(design.source.layers[0]?.id ?? null);

  const patternOptions = useMemo(
    () =>
      listPatterns()
        .slice()
        .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name))
        .map((pattern) => ({ value: pattern.id, label: `${pattern.name} — ${pattern.category}` })),
    [],
  );

  const layers = design.source.layers;
  const pitch = design.panel.size + design.panel.gap;

  return (
    <div className="layers-tab">
      <div className="layer-list">
        {layers.map((layer, index) => (
          <LayerCard
            key={layer.id}
            layer={layer}
            index={index}
            total={layers.length}
            isBottom={index === 0}
            expanded={expanded === layer.id}
            pitchMm={pitch}
            assets={design.assets}
            patternOptions={patternOptions}
            onToggleExpand={() => setExpanded(expanded === layer.id ? null : layer.id)}
            onUpdate={(patch) => updateLayer(layer.id, patch)}
            onUpdateParams={(params) => updateLayerParams(layer.id, params)}
            onRemove={() => removeLayer(layer.id)}
            onMove={(direction) => moveLayer(layer.id, direction)}
            onRequestImage={onRequestImage}
          />
        ))}
      </div>

      <div className="layer-add">
        <Select
          label="Add a layer"
          value=""
          options={[{ value: '', label: 'Choose a pattern…' }, ...patternOptions]}
          onChange={(patternId) => {
            if (!patternId) return;
            addLayer(patternId);
            setExpanded(null);
          }}
        />
      </div>

      <p className="hint">
        Layers composite from the top of this list downwards. The bottom layer defines the base
        field, so its blend mode has nothing to blend against and acts as <strong>Replace</strong>.
      </p>
    </div>
  );
}

interface LayerCardProps {
  layer: PatternLayer;
  index: number;
  total: number;
  isBottom: boolean;
  expanded: boolean;
  pitchMm: number;
  assets: ReturnType<typeof useDesignStore.getState>['design']['assets'];
  patternOptions: ReadonlyArray<{ value: string; label: string }>;
  onToggleExpand(): void;
  onUpdate(patch: Partial<PatternLayer>): void;
  onUpdateParams(params: Record<string, number | string | boolean>): void;
  onRemove(): void;
  onMove(direction: -1 | 1): void;
  onRequestImage(): void;
}

function LayerCard({
  layer,
  index,
  total,
  isBottom,
  expanded,
  pitchMm,
  assets,
  patternOptions,
  onToggleExpand,
  onUpdate,
  onUpdateParams,
  onRemove,
  onMove,
  onRequestImage,
}: LayerCardProps) {
  const definition = requirePattern(layer.patternId);
  const [showTransform, setShowTransform] = useState(false);

  return (
    <div
      className={`layer-card${expanded ? ' is-expanded' : ''}${layer.enabled ? '' : ' is-muted'}`}
    >
      <div className="layer-card__head">
        <Thumbnail
          layers={[{ ...layer, enabled: true, amount: 1, blend: 'replace' }]}
          size={48}
          panels={1}
        />

        <button type="button" className="layer-card__title" onClick={onToggleExpand}>
          <span className="layer-card__name">{definition.name}</span>
          <span className="layer-card__meta">
            {isBottom ? 'base' : layer.blend}
            {layer.amount < 1 ? ` · ${Math.round(layer.amount * 100)}%` : ''}
          </span>
        </button>

        <div className="layer-card__actions">
          <button
            type="button"
            className="icon-button icon-button--small"
            title={layer.enabled ? 'Mute this layer' : 'Unmute this layer'}
            aria-pressed={!layer.enabled}
            onClick={() => onUpdate({ enabled: !layer.enabled })}
          >
            {layer.enabled ? '◉' : '○'}
          </button>
          <button
            type="button"
            className="icon-button icon-button--small"
            title="Move down"
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            ↓
          </button>
          <button
            type="button"
            className="icon-button icon-button--small"
            title="Move up"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="icon-button icon-button--small icon-button--danger"
            title="Remove this layer"
            onClick={onRemove}
          >
            ×
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="layer-card__body">
          <p className="layer-card__description">{definition.description}</p>

          <Select
            label="Pattern"
            value={layer.patternId}
            options={patternOptions}
            onChange={(patternId) => {
              if (patternId === layer.patternId) return;
              // Params belong to the old schema, so start the new one from its
              // own defaults rather than carrying incompatible keys across.
              onUpdate({ patternId, params: {} });
            }}
          />

          {!isBottom ? (
            <>
              <Select<BlendMode>
                label="Blend"
                value={layer.blend}
                options={BLEND_MODES}
                onChange={(blend) => onUpdate({ blend })}
              />
              <Slider
                label="Amount"
                value={layer.amount}
                min={0}
                max={1}
                step={0.01}
                onChange={(amount) => onUpdate({ amount })}
              />
            </>
          ) : null}

          <ParamControls
            schema={definition.params}
            values={layer.params}
            pitchMm={pitchMm}
            assets={assets}
            onChange={(key, value) => onUpdateParams({ [key]: value })}
            onRequestImage={onRequestImage}
          />

          <button
            type="button"
            className="button button--ghost"
            onClick={() => setShowTransform((open) => !open)}
          >
            {showTransform ? 'Hide' : 'Show'} placement
          </button>

          {showTransform ? (
            <Section
              title="Placement"
              description="Moves the motif in wall space. Panel-local patterns keep their own alignment."
            >
              <AngleDial
                label="Rotation"
                value={layer.transform.rotation}
                onChange={(rotation) => onUpdate({ transform: { ...layer.transform, rotation } })}
              />
              <Slider
                label="Scale"
                value={layer.transform.scale}
                min={0.1}
                max={5}
                step={0.05}
                help="Above 1 enlarges the motif."
                onChange={(scale) => onUpdate({ transform: { ...layer.transform, scale } })}
              />
              <Slider
                label="Offset X"
                value={layer.transform.offsetX}
                min={-500}
                max={500}
                step={1}
                unit="mm"
                onChange={(offsetX) => onUpdate({ transform: { ...layer.transform, offsetX } })}
              />
              <Slider
                label="Offset Y"
                value={layer.transform.offsetY}
                min={-500}
                max={500}
                step={1}
                unit="mm"
                onChange={(offsetY) => onUpdate({ transform: { ...layer.transform, offsetY } })}
              />
              <Toggle
                label="Mirror X"
                checked={layer.transform.mirrorX}
                onChange={(mirrorX) => onUpdate({ transform: { ...layer.transform, mirrorX } })}
              />
              <Toggle
                label="Mirror Y"
                checked={layer.transform.mirrorY}
                onChange={(mirrorY) => onUpdate({ transform: { ...layer.transform, mirrorY } })}
              />
            </Section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
